import { ArrowRight, Coins, Package, TrendingUp, Zap } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import type { MyFortAgentItem } from "@/api/types";
import { C } from "@/components/modal/home";
import { getTokenName } from "@/config/common";
import { CCC_AIF_ADDRESS } from "@/config/env";
import { AGENT_IMAGE_URL_INACTIVE } from "./constants";
import { HomeMyAgentActivatedCardV2 } from "./HomeMyAgentActivatedCardV2";
import { HomeMyAgentInactiveCard } from "./HomeMyAgentInactiveCard";
import { MyAgentsDialogsHost } from "./MyAgentsDialogsHost";
import { isAgentActivated, pickHomeDisplayAgent } from "./pickHomeDisplayAgent";

export type HomeMyAgentsSectionBodyV2Props = {
  hasUser: boolean;
  myAgentsLoading: boolean;
  agents: MyFortAgentItem[] | undefined;
  onConnectWallet: () => void;
  onGoBuyMarket: () => void;
  onAddress: (agent: MyFortAgentItem) => void;
  onDeliveryStatus: (agent: MyFortAgentItem) => void;
  onGoManage: () => void;
  onIncomeHistory: (agent: MyFortAgentItem) => void;
};

function EmptyCard({
  badge,
  title,
  subtitle,
  children,
}: {
  badge: React.ReactNode;
  title: string;
  subtitle: string;
  children?: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div
      className="rounded-xl overflow-hidden relative"
      style={{
        background:
          "linear-gradient(135deg, rgba(100,140,200,0.015) 0%, #0c1020 40%, #080c18 100%)",
        border: "1px solid rgba(100,140,200,0.06)",
        boxShadow: "0 2px 20px rgba(100,140,200,0.02)",
      }}
    >
      <div
        className="h-px"
        style={{
          background:
            "linear-gradient(90deg, transparent 10%, rgba(100,140,200,0.08) 50%, transparent 90%)",
        }}
      />
      <div className="flex" style={{ minHeight: 0 }}>
        <div
          className="relative flex flex-col items-center justify-center overflow-hidden"
          style={{
            width: "42%",
            minHeight: "180px",
            backgroundColor: "#0a0e1a",
            backgroundImage:
              "radial-gradient(ellipse at center 60%, rgba(100,140,200,0.08) 0%, #0a0e1a 60%)",
          }}
        >
          <div className="absolute inset-0 pointer-events-none">
            <div
              className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-28 h-28 rounded-full blur-[50px]"
              style={{ background: "rgba(100,140,200,0.08)" }}
            />
          </div>
          <div
            className="relative z-10 flex items-center justify-center"
            style={{ width: "90%", height: "90%" }}
          >
            <img
              src={AGENT_IMAGE_URL_INACTIVE}
              alt=""
              className="w-full h-full object-contain"
              style={{ opacity: 0.4 }}
            />
          </div>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-16 h-1">
            <div
              className="w-full h-full rounded-full"
              style={{
                background:
                  "radial-gradient(ellipse, rgba(34,197,94,0.10) 0%, transparent 70%)",
              }}
            />
          </div>
        </div>

        <div
          className="flex-1 py-3.5 pr-3.5 pl-1 space-y-2.5"
          style={{ minWidth: 0 }}
        >
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span
                className="font-black text-base tracking-wide"
                style={{ color: C.muted }}
              >
                {title}
              </span>
              <span
                className="flex items-center gap-1 px-1.5 py-[2px] rounded text-xs font-bold ml-auto"
                style={{
                  background: "rgba(100,116,139,0.06)",
                  color: C.dim,
                  border: "1px solid rgba(100,116,139,0.10)",
                }}
              >
                {badge}
              </span>
            </div>
            <div className="mt-1 text-xs" style={{ color: C.dim }}>
              {subtitle}
            </div>
          </div>
          <div
            className="rounded-lg p-2.5 space-y-1.5"
            style={{
              background: "rgba(34,197,94,0.03)",
              border: "1px solid rgba(34,197,94,0.06)",
            }}
          >
            <div className="text-xs leading-relaxed" style={{ color: C.muted }}>
              <Trans
                i18nKey="homePage.myAgents.emptyIntroDescription"
                components={{
                  bullet: <span style={{ color: C.primary }} />,
                  strong: (
                    <span className="font-semibold" style={{ color: C.text }} />
                  ),
                }}
              />
            </div>
            <div
              className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]"
              style={{ color: C.dim }}
            >
              <span className="flex items-center gap-1">
                <Zap size={10} style={{ color: C.primary }} />
                {t("homePage.myAgents.emptyIntroCustodyBoost")}
              </span>
              <span className="flex items-center gap-1">
                <TrendingUp size={10} style={{ color: "#34d399" }} />
                {t("homePage.myAgents.emptyIntroRelease")}
              </span>
              <span className="flex items-center gap-1">
                <Coins size={10} style={{ color: "#60a5fa" }} />
                {t("homePage.myAgents.emptyIntroTokenRange", {
                  tokenNameAif: getTokenName(CCC_AIF_ADDRESS),
                })}
              </span>
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

export function HomeMyAgentsSectionBodyV2({
  hasUser,
  agents,
  onConnectWallet,
  onGoBuyMarket,
  onAddress,
  onDeliveryStatus,
  onGoManage,
  onIncomeHistory,
}: HomeMyAgentsSectionBodyV2Props) {
  const { t } = useTranslation();

  if (!hasUser) {
    return (
      <EmptyCard
        badge={
          <>
            <Package size={8} />
            {t("homePage.myAgents.badgeNotConnected")}
          </>
        }
        title={t("homePage.myAgents.summonTitle")}
        subtitle={t("homePage.myAgents.connectPrompt")}
      >
        <button
          type="button"
          className="w-full py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 btn-primary transition-all active:scale-95"
          onClick={onConnectWallet}
        >
          {t("homePage.myAgents.connectWallet")}
        </button>
      </EmptyCard>
    );
  }

  if ((agents?.length ?? 0) === 0) {
    return (
      <EmptyCard
        badge={
          <>
            <Package size={8} />
            {t("homePage.myAgents.badgeNotOwned")}
          </>
        }
        title={t("homePage.myAgents.summonTitle")}
        subtitle={t("homePage.myAgents.summonSubtitle")}
      >
        <button
          type="button"
          className="w-full py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 btn-primary transition-all active:scale-95"
          onClick={onGoBuyMarket}
        >
          {t("homePage.myAgents.goBuyMarket")}
          <ArrowRight size={12} />
        </button>
      </EmptyCard>
    );
  }

  const agentList = agents ?? [];
  const displayAgent = pickHomeDisplayAgent(agentList);
  if (!displayAgent) return null;

  return (
    <>
      {isAgentActivated(displayAgent) ? (
        <HomeMyAgentActivatedCardV2
          agent={displayAgent}
          onAddress={onAddress}
          onDeliveryStatus={onDeliveryStatus}
          onGoManage={onGoManage}
          onIncomeHistory={onIncomeHistory}
        />
      ) : (
        <HomeMyAgentInactiveCard agent={displayAgent} onGoManage={onGoManage} />
      )}
      <MyAgentsDialogsHost agents={agentList} />
    </>
  );
}
